import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, Sparkles, HardDrive, Layers, Coins, PlayCircle, Loader2, Rocket } from "lucide-react";

export default function Analytics() {
  const { user, isOwner } = useAuth();
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/analytics/me").then((r) => setD(r.data)).catch(() => setD(null));
  }, []);

  if (!d) {
    return (
      <div className="p-12 text-zinc-500 text-sm flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} /> Loading your studio metrics…
      </div>
    );
  }

  const tierColors = {
    smart: "bg-amber-500", budget: "bg-emerald-500", premium: "bg-sky-500",
    ultra: "bg-fuchsia-500", preview: "bg-zinc-500", manual: "bg-white/60", unknown: "bg-white/20",
  };
  const tierOrder = ["smart", "budget", "premium", "ultra", "preview", "manual", "unknown"];
  const totalTier = Object.values(d.tier_mix).reduce((a, b) => a + b, 0) || 1;
  const isStudio = d.is_studio_plus || isOwner;

  return (
    <div className="p-6 md:p-10 lg:p-14 max-w-[1400px]">
      <div className="mb-10">
        <div className="font-mono-tech text-[10px] text-amber-400 mb-3 flex items-center gap-2">
          <TrendingUp className="w-3 h-3" strokeWidth={1.75} /> STUDIO · ANALYTICS
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-light leading-none">
          Your studio, <span className="font-bold text-amber-400">in numbers.</span>
        </h1>
        <p className="text-zinc-400 mt-4 max-w-2xl">
          The last 30 days at a glance — what you shipped, what it cost in credits, which engines your work loves best.
        </p>
      </div>

      {/* Studio+ tag */}
      {isStudio ? (
        <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 text-[10px] font-mono-tech uppercase mb-6">
          <Rocket className="w-3 h-3 mr-1.5" strokeWidth={2} /> Studio · Full analytics unlocked
        </Badge>
      ) : (
        <div className="mb-6 p-4 rounded-lg border border-amber-500/30 bg-amber-500/5 flex items-center gap-3">
          <Sparkles className="w-4 h-4 text-amber-400" strokeWidth={1.75} />
          <div className="text-sm text-zinc-300 flex-1">
            Basic metrics shown. Studio unlocks per-workspace analytics, team activity breakdowns and CSV export.
          </div>
          <Link to="/pricing" className="text-xs font-mono-tech uppercase text-amber-400 hover:text-amber-300">
            Upgrade →
          </Link>
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="analytics-kpis">
        <Kpi label="COMPLETED · 30D" value={d.projects_completed} icon={PlayCircle} accent testid="kpi-completed" />
        <Kpi label="CREDITS · SPENT · 30D" value={d.credits_spent_30d.toLocaleString()} icon={Coins} testid="kpi-credits-spent" />
        <Kpi label="CREDITS · BALANCE" value={d.credits_balance.toLocaleString()} icon={Coins} color="emerald" testid="kpi-balance" />
        <Kpi label="STORAGE · USED" value={`${d.storage_mb.toFixed(1)} MB`} icon={HardDrive} testid="kpi-storage" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
        <Kpi label="ALL · TIME · PROJECTS" value={d.projects_all_time} icon={Layers} testid="kpi-all-time" />
        <Kpi label="IN · PROGRESS" value={d.projects_processing} icon={Loader2} testid="kpi-processing" />
        <Kpi label="WORKSPACES" value={d.workspaces_count} icon={Layers} testid="kpi-workspaces" />
        <Kpi label="TIER" value={(isOwner ? "OWNER" : (d.tier || "free")).toUpperCase()} icon={Rocket} color="amber" testid="kpi-tier" />
      </div>

      {/* Tier mix + Top providers */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
        <div className="p-6 rounded-lg border border-white/5 bg-[#121214]" data-testid="tier-mix-chart">
          <div className="font-mono-tech text-[10px] text-zinc-500 mb-4">YOUR · TIER · MIX (last 30d)</div>
          {totalTier <= 1 ? (
            <div className="text-sm text-zinc-500">No completed videos yet — <Link to="/projects/new" className="text-amber-400 hover:text-amber-300">start your first project →</Link></div>
          ) : (
            <div className="space-y-2">
              {tierOrder.filter((k) => (d.tier_mix[k] || 0) > 0).map((k) => {
                const c = d.tier_mix[k];
                const pct = (c / totalTier) * 100;
                return (
                  <div key={k} className="flex items-center gap-3">
                    <div className="w-20 text-sm font-medium capitalize">{k}</div>
                    <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                      <div className={`h-full ${tierColors[k] || "bg-white/40"}`} style={{ width: `${Math.min(100, pct)}%` }} />
                    </div>
                    <div className="text-xs font-mono-tech text-zinc-400 w-14 text-right">{pct.toFixed(0)}%</div>
                    <div className="text-xs font-mono-tech text-zinc-500 w-10 text-right">{c}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="p-6 rounded-lg border border-white/5 bg-[#121214]" data-testid="top-providers-chart">
          <div className="font-mono-tech text-[10px] text-zinc-500 mb-4">TOP · ENGINES (last 30d)</div>
          {d.top_providers.length === 0 ? (
            <div className="text-sm text-zinc-500">No completed videos yet.</div>
          ) : (
            <div className="space-y-2">
              {d.top_providers.map(({ name, count }) => {
                const total = d.top_providers.reduce((a, b) => a + b.count, 0);
                const pct = total ? (count / total) * 100 : 0;
                return (
                  <div key={name} className="flex items-center gap-3">
                    <div className="w-32 text-sm font-medium truncate" title={name}>{name}</div>
                    <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full bg-amber-500" style={{ width: `${Math.min(100, pct)}%` }} />
                    </div>
                    <div className="text-xs font-mono-tech text-zinc-500 w-10 text-right">{count}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, accent, color, testid }) {
  const colorCls = accent ? "text-amber-400"
    : color === "emerald" ? "text-emerald-400"
    : color === "amber" ? "text-amber-400"
    : "text-white";
  return (
    <div className="p-4 rounded-lg border border-white/5 bg-[#121214]" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="font-mono-tech text-[9px] text-zinc-500">{label}</div>
        {Icon && <Icon className={`w-4 h-4 ${colorCls}`} strokeWidth={1.5} />}
      </div>
      <div className={`font-heading text-2xl font-bold tracking-tight ${colorCls}`}>{value}</div>
    </div>
  );
}
